import { useAndie } from '@/providers/andie-provider';
import { AddRounded, CloseRounded, HistoryRounded, KeyboardTabRounded } from '@mui/icons-material';
import { Divider, Drawer, IconButton, Stack, Tooltip } from '@mui/material';
import { styled, useColorScheme } from '@mui/material/styles';
import AndieChat from './chat/AndieChat';
import AndieChatHistory from './chat/AndieChatHistory';
import { AndieChatManagement } from './chat/AndieChatManagement';
import { useTranslation } from 'react-i18next';
import { memo, useEffect, useRef, useState } from 'react';
import { useThemeProvider } from '@/providers/theme-provider';
import { useAndieChatControls, useAndieChatMessages } from '@/providers/andie-chat-provider';
import type { UIMessage } from 'ai';

const andieDrawerWidth = 450;

const ExtendIcon = styled(KeyboardTabRounded, {
    shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
    transform: expanded ? 'rotate(0)' : 'rotate(180deg)',
}));

const getMessageText = (message: UIMessage) => {
    const legacyContent = (message as UIMessage & { content?: unknown }).content;
    if (typeof legacyContent === 'string') {
        return legacyContent;
    }

    return message.parts
        .filter((part) => part.type === 'text')
        .map((part) => part.text)
        .join('\n');
};

const hasUserAuthoredMessage = (messages: UIMessage[]) =>
    messages.some((message) => {
        if (message.role !== 'user') return false;

        const text = getMessageText(message).trim();
        return text.length > 0 && !text.startsWith('<hidden-message>');
    });

const AndieDrawer = memo(() => {
    const { t } = useTranslation();
    const { setBreakpointsStealPixels } = useThemeProvider();
    const { open, setOpen, processingFirstMessage, inputValueRef } = useAndie();
    const { messages } = useAndieChatMessages();
    const chatControls = useAndieChatControls();
    const { mode, systemMode } = useColorScheme();
    const [historyOpen, setHistoryOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const setDeleteDialogOpen = setHistoryOpen;

    const resolvedMode = mode === 'system' ? systemMode : mode;
    const logoSrc = resolvedMode === 'dark' ? '/andie-logo-contrast.png' : '/andie-logo.png';
    const drawerWidth = expanded ? andieDrawerWidth * 2 : andieDrawerWidth;
    const hasUserAuthoredMessageRef = useRef(false);
    hasUserAuthoredMessageRef.current = hasUserAuthoredMessage(messages);

    useEffect(() => {
        if (open) {
            setBreakpointsStealPixels(drawerWidth);
        } else {
            setBreakpointsStealPixels(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, drawerWidth]);

    const handleNewChat = () => {
        if (!hasUserAuthoredMessageRef.current) {
            return;
        }

        processingFirstMessage.current = false;
        inputValueRef.current = '';
        hasUserAuthoredMessageRef.current = false;
        // Shared reset (stop + clear + new thread id + hydrate). AndieChatManagement persists the
        // new id to localStorage via its chatId effect, so it's restored as current on reload.
        chatControls.startNewConversation();
        setDeleteDialogOpen(false);
    };

    return (
        <>
            <Drawer
                variant="persistent"
                anchor="right"
                open={open}
                sx={(theme) => ({
                    width: open ? drawerWidth : 0,
                    flexShrink: 0,
                    zIndex: theme.zIndex.drawer - 2,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        height: 'calc(100% - 41px)',
                        top: '41px',
                        transition: theme.transitions.create('width', {
                            duration: theme.transitions.duration.shorter,
                        }),
                    },
                    [theme.breakpoints.down('sm')]: {
                        width: 0,
                        '& .MuiDrawer-paper': {
                            width: '100%',
                        },
                    },
                })}
                transitionDuration={0}
            >
                <Stack
                    divider={<Divider orientation="horizontal" flexItem />}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                    <Stack
                        direction={'row'}
                        alignItems="center"
                        justifyContent="space-between"
                        px={2}
                        py={1}
                        height={44}
                    >
                        <img src={logoSrc} alt={t('andie.chat.title')} height={28} />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={t(expanded ? 'str.collapse' : 'str.extend')}>
                                <IconButton
                                    sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                                    onClick={() => setExpanded((current) => !current)}
                                >
                                    <ExtendIcon expanded={expanded} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('andie.chat.new.tooltip', { defaultValue: 'New chat' })}>
                                <IconButton
                                    onClick={() => {
                                        handleNewChat();
                                        setHistoryOpen(false);
                                    }}
                                >
                                    <AddRounded />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                title={t('andie.chat.history.tooltip', { defaultValue: 'Chat history' })}
                            >
                                <IconButton
                                    color={historyOpen ? 'primary' : 'default'}
                                    onClick={() => setHistoryOpen((current) => !current)}
                                >
                                    <HistoryRounded />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('str.minimize')}>
                                <IconButton
                                    onClick={() => {
                                        setHistoryOpen(false);
                                        setOpen(false);
                                    }}
                                >
                                    <CloseRounded />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    <Stack sx={{ height: '100%', flex: 1, minHeight: 0 }}>
                        <AndieChatManagement />
                        {historyOpen ? (
                            <AndieChatHistory
                                onClose={() => setHistoryOpen(false)}
                                onNewChat={() => {
                                    handleNewChat();
                                    setHistoryOpen(false);
                                }}
                            />
                        ) : (
                            <AndieChat />
                        )}
                    </Stack>
                </Stack>
            </Drawer>
        </>
    );
});

export default AndieDrawer;
export { andieDrawerWidth };
